'use client';

import { useState, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { X, Database, ChevronDown, ChevronRight, Clock, Loader2 } from 'lucide-react';
import { dataDeskOpenAtom, currentConversationIdAtom } from '@/_state/atoms';
import { useDataPoolQuery } from '@/_state/queries';
import { EDataSource, IDataPoolEntry } from '@/types';
import { Button } from '@/_components/UIComponents/Button';
import { Sheet, SheetContent, SheetTitle } from '@/_components/UIComponents/Sheet';
import { getDataSourceDisplay } from '@/_utils/dataSourceDisplay';
import JsonViewer from '@/_components/JsonViewer/JsonViewer';

const DESKTOP_BREAKPOINT = 1280;

function useIsCompact() {
	const [isCompact, setIsCompact] = useState(false);

	useEffect(() => {
		const check = () => window.innerWidth < DESKTOP_BREAKPOINT;
		setIsCompact(check());

		const mql = window.matchMedia(`(max-width: ${DESKTOP_BREAKPOINT - 1}px)`);
		const onChange = () => setIsCompact(check());
		mql.addEventListener('change', onChange);
		return () => mql.removeEventListener('change', onChange);
	}, []);

	return isCompact;
}

const ALL_SOURCES = [
	EDataSource.SUBSCAN,
	EDataSource.COINGECKO,
	EDataSource.POLKASSEMBLY,
	EDataSource.SUBSQUARE,
	EDataSource.POLKADOT_WIKI,
	EDataSource.INDEXER
];

function DataEntryCard({ entry }: { entry: IDataPoolEntry }) {
	const [expanded, setExpanded] = useState(false);
	const { name, colorClass } = getDataSourceDisplay(entry.dataSource);
	const time = entry.fetchedAt.split('T')[1]?.replace('Z', '').split('.')[0] ?? '';

	return (
		<div className='border-border/50 min-w-0 overflow-hidden rounded-lg border p-3'>
			{/* Header row: source badge + endpoint + failed badge */}
			<div className='flex items-center gap-2'>
				<span
					className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}
				>
					{name}
				</span>
				{entry.endpoint && (
					<span className='text-muted-foreground min-w-0 truncate font-mono text-xs'>{entry.endpoint}</span>
				)}
				{entry.failed && (
					<span className='shrink-0 rounded-full bg-red-500/20 px-1.5 py-0.5 text-xs text-red-400'>Failed</span>
				)}
			</div>

			{/* Step + time row */}
			<div className='text-muted-foreground mt-1.5 flex items-center gap-3 text-xs'>
				<span>Step {entry.stepIndex + 1}</span>
				<span className='flex shrink-0 items-center gap-1'>
					<Clock className='h-3 w-3' />
					{time}
				</span>
			</div>

			{/* Step description — own line, wraps naturally */}
			{entry.stepDescription && (
				<p className='text-muted-foreground mt-1 text-xs leading-relaxed break-words'>{entry.stepDescription}</p>
			)}

			{/* Extracted value preview */}
			{entry.extractedValue && (
				<p className='text-muted-foreground mt-1.5 truncate text-xs italic'>{entry.extractedValue}</p>
			)}

			{/* Expandable JSON preview */}
			<button
				type='button'
				onClick={() => setExpanded(!expanded)}
				className='text-muted-foreground hover:text-foreground mt-2 flex w-full items-center gap-1 text-xs transition-colors'
			>
				{expanded ? <ChevronDown className='h-3 w-3' /> : <ChevronRight className='h-3 w-3' />}
				<span>{expanded ? 'Hide' : 'Preview'} data</span>
			</button>

			{expanded && (
				<div className='bg-muted/50 mt-2 max-h-48 overflow-auto rounded-md p-2 font-mono text-xs leading-relaxed'>
					<JsonViewer
						data={entry.rawData}
						defaultExpandDepth={1}
					/>
				</div>
			)}
		</div>
	);
}

function DataDeskContent({ onClose, staticEntries }: { onClose: () => void; staticEntries?: IDataPoolEntry[] }) {
	const conversationId = useAtomValue(currentConversationIdAtom);
	const { data: queriedEntries = [], isLoading: isQueryLoading } = useDataPoolQuery(
		staticEntries ? null : conversationId
	);

	// Use static entries (public page) or queried entries (owner page)
	const entries = staticEntries ?? queriedEntries;
	const isLoading = staticEntries ? false : isQueryLoading;
	const [activeFilter, setActiveFilter] = useState<EDataSource | null>(null);

	const filteredEntries = activeFilter ? entries.filter((e) => e.dataSource === activeFilter) : entries;
	const uniqueSources = new Set(entries.map((e) => e.dataSource));

	return (
		<div className='flex h-full flex-col'>
			{/* Header */}
			<div className='border-border/50 flex items-center justify-between border-b px-4 py-3'>
				<div className='flex items-center gap-2'>
					<Database className='text-muted-foreground h-4 w-4' />
					<h2 className='text-sm font-semibold'>Data Desk</h2>
				</div>
				<Button
					variant='ghost'
					size='icon-sm'
					onClick={onClose}
					className='text-muted-foreground hover:text-foreground'
					aria-label='Close Data Desk'
				>
					<X className='h-4 w-4' />
				</Button>
			</div>

			{/* Filter chips */}
			<div className='border-border/50 flex flex-wrap gap-1.5 border-b px-4 py-2.5'>
				<button
					type='button'
					onClick={() => setActiveFilter(null)}
					className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
						activeFilter === null
							? 'bg-foreground/10 text-foreground'
							: 'bg-muted text-muted-foreground hover:text-foreground'
					}`}
				>
					All
				</button>
				{ALL_SOURCES.filter((s) => uniqueSources.has(s)).map((source) => {
					const { name, colorClass } = getDataSourceDisplay(source);
					const isActive = activeFilter === source;
					return (
						<button
							key={source}
							type='button'
							onClick={() => setActiveFilter(isActive ? null : source)}
							className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
								isActive ? colorClass : 'bg-muted text-muted-foreground hover:text-foreground'
							}`}
						>
							{name}
						</button>
					);
				})}
			</div>

			{/* Stats bar */}
			<div className='text-muted-foreground border-border/50 border-b px-4 py-2 text-xs'>
				{entries.length} fetches · {uniqueSources.size} sources
			</div>

			{/* Scrollable data entries */}
			<div className='flex-1 overflow-x-hidden overflow-y-auto p-3'>
				{isLoading ? (
					<div className='flex flex-col items-center gap-2 py-12'>
						<Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
					</div>
				) : (
					<div className='flex flex-col gap-2.5'>
						{filteredEntries.map((entry) => (
							<DataEntryCard
								key={entry.id}
								entry={entry}
							/>
						))}
						{filteredEntries.length === 0 && (
							<div className='text-muted-foreground flex flex-col items-center gap-2 py-12 text-center text-sm'>
								<Database className='h-8 w-8 opacity-40' />
								<p>{activeFilter ? 'No data from this source' : 'No data fetched yet'}</p>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export default function DataDesk({ entries }: { entries?: IDataPoolEntry[] } = {}) {
	const [isOpen, setIsOpen] = useAtom(dataDeskOpenAtom);
	const isCompact = useIsCompact();

	// Compact screens: use Sheet overlay
	if (isCompact) {
		return (
			<Sheet
				open={isOpen}
				onOpenChange={setIsOpen}
			>
				<SheetContent
					side='right'
					className='w-[85vw] p-0 sm:max-w-[400px] [&>button:last-child]:hidden'
				>
					<SheetTitle className='sr-only'>Data Desk</SheetTitle>
					<DataDeskContent
						onClose={() => setIsOpen(false)}
						staticEntries={entries}
					/>
				</SheetContent>
			</Sheet>
		);
	}

	// Desktop: inline collapsible panel
	return (
		<aside
			className={`border-border/50 bg-background flex-shrink-0 border-l transition-all duration-300 ease-in-out ${
				isOpen ? 'w-[380px]' : 'w-0 overflow-hidden border-l-0'
			}`}
		>
			<div className='h-full w-[380px]'>
				<DataDeskContent
					onClose={() => setIsOpen(false)}
					staticEntries={entries}
				/>
			</div>
		</aside>
	);
}
